import { matchRoutes, RouteObject } from 'react-router-dom';
import type { PageContext } from '@api/assistant/assistantApi';

/**
 * Every generouted page sits under the optional `-[lang]` folder, so each pattern starts with `/:lang?`
 * and carries a `lang` param. The language says nothing about which page this is, so both are dropped:
 * the assistant matches `/supplier-notifications/:supplierNotificationId`, not `/:lang?/…`.
 */
const LANG_PREFIX = /^\/:lang\?(?=\/|$)/;
/** `lang` is the language; `*` is a splat (the 404 route), which holds the actual path. */
const DROPPED_PARAMS = new Set(['lang', '*']);

/**
 * The matched route's pattern and params — never the actual path or the query string. Joins the
 * `path` of every matched route (generouted nests them as relative segments), and takes the params of
 * the deepest match, which has them all.
 */
export const resolveRoute = (routes: RouteObject[] | null, pathname: string): PageContext['route'] => {
  if (!routes) return null;
  const matches = matchRoutes(routes, pathname);
  if (!matches?.length) return null;

  const joined = matches
    .map(match => match.route.path)
    .filter((path): path is string => !!path)
    .join('/');
  const pattern =
    `/${joined}`
      .replace(/\/{2,}/g, '/')
      .replace(/(.)\/$/, '$1')
      .replace(LANG_PREFIX, '') || '/';

  const params: Record<string, string> = {};
  Object.entries(matches[matches.length - 1].params).forEach(([name, value]) => {
    if (value !== undefined && !DROPPED_PARAMS.has(name)) params[name] = value;
  });
  return { pattern, params };
};
